import { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting.js";
import { useTheme } from "@mui/material";
import { MyTheme } from "./../colors";

export const RoseChart = ({ chartData, startDate, endDate, maximize }) => {
    const chartDivId = useRef(`rosechart-${Math.random().toString(36).substring(2, 11)}`);
    const rootRef = useRef(null);
    const theme = useTheme();
    const graphicTitle = chartData.title;

    useEffect(() => {
        if (rootRef.current) {
            rootRef.current.dispose();
            rootRef.current = null;
        }

        const generateChart = () => {
            try {
                let newChart = null;
                const startMs = new Date(startDate).getTime();
                const endMs = new Date(endDate).getTime();
                const endDatePlusOneDay = new Date(endMs);
                endDatePlusOneDay.setDate(endDatePlusOneDay.getDate() + 1);

                // --- 1. Crear root y aplicar tema de colores ---
                const root = am5.Root.new(chartDivId.current);
                rootRef.current = root;
                root.setThemes([MyTheme.new(root)]);

                // --- 2. Crear PieChart (base del Rose) ---
                // Un Rose Chart es un grafico de pastel donde el radio de cada slice
                // es proporcional a su valor, combinando angulo y radio variable.
                newChart = root.container.children.push(
                    am5percent.PieChart.new(root, {
                        layout: root.verticalLayout,
                        innerRadius: am5.percent(20),
                    })
                );

                // --- 3. Crear la serie PieSeries ---
                const series = newChart.series.push(
                    am5percent.PieSeries.new(root, {
                        name: "Series",
                        valueField: "value",
                        categoryField: "category",
                    })
                );

                // --- 4. Procesar datos dinamicos desde chartData ---
                // Mismo patron que NightingaleChart: filtra por rango de fechas
                const processedData = chartData.data
                    .map((item) => {
                        const filteredValues = item.data[0].filter((value, index) => {
                            const date = new Date(
                                Date.now() - parseInt(item.data[1][index]) * 24 * 60 * 60 * 1000
                            ).getTime();
                            if (startMs === 0 && endMs === 0) {
                                return true;
                            } else {
                                return date >= startMs && date <= endDatePlusOneDay;
                            }
                        });
                        const count = filteredValues.filter((value) => value > 0).length;
                        return count > 0 ? { category: item.label, value: count } : null;
                    })
                    .filter(Boolean);

                const totalSum = processedData.reduce((sum, item) => sum + (item.value || 0), 0);

                // --- 5. Radio proporcional al valor ---
                // El radio de cada slice se escala proporcionalmente a su valor vs el total
                series.slices.template.adapters.add("radius", (radius, target) => {
                    const dataItem = target.dataItem;
                    if (!dataItem) return radius;
                    const value = dataItem.get("valueWorking", 0);
                    const maxRadius = series.getPrivate("radius", 100);
                    const scale = totalSum > 0 ? value / totalSum : 1;
                    return maxRadius * Math.sqrt(scale);
                });

                // --- 6. Estilo de los slices: bordes redondeados ---
                series.slices.template.setAll({
                    strokeOpacity: 0.5,
                    strokeWidth: 1,
                    fillOpacity: 0.8,
                    cornerRadius: 5,
                });

                // Asignar color diferente a cada slice usando el ColorSet del tema
                series.slices.template.adapters.add("fill", function (fill, target) {
                    return newChart.get("colors").getIndex(series.dataItems.indexOf(target.dataItem));
                });

                // --- 7. Labels externos con nombre + porcentaje ---
                series.labels.template.setAll({
                    text: "{category}: {valuePercentTotal.formatNumber('0.0')}%",
                    fontSize: 12,
                });
                series.ticks.template.setAll({
                    visible: true,
                });

                // Bullets con valor numerico dentro de cada slice
                const textColor = am5.color(theme.palette.text.primary);
                series.bullets.push(function () {
                    return am5.Bullet.new(root, {
                        sprite: am5.Label.new(root, {
                            text: "{value}",
                            populateText: true,
                            centerX: am5.percent(50),
                            centerY: am5.percent(50),
                            fill: textColor,
                            fontSize: 14,
                        }),
                    });
                });

                // --- 8. Setear datos ---
                series.data.setAll(processedData);

                // --- 9. Leyenda ---
                const legend = newChart.children.push(
                    am5.Legend.new(root, {
                        centerX: am5.p50,
                        x: am5.p50,
                        marginTop: 15,
                    })
                );
                series.events.on("datavalidated", function () {
                    legend.data.setAll(series.dataItems);
                });

                // --- 10. Titulo oculto (visible solo al exportar PDF) ---
                const title = newChart.children.push(
                    am5.Label.new(root, {
                        text: graphicTitle,
                        fontSize: 20,
                        x: am5.percent(50),
                        y: am5.percent(0),
                        centerX: am5.percent(50),
                        visible: false,
                    })
                );

                // --- 11. Exportacion (PNG, PDF, etc.) ---
                const exporting = am5plugins_exporting.Exporting.new(root, {
                    menu: am5plugins_exporting.ExportingMenu.new(root, {
                        align: "right",
                        valign: "bottom",
                    }),
                    pdfOptions: {
                        addURL: false,
                    },
                });

                exporting.events.on("exportstarted", function (event) {
                    if (event.format === "pdf") {
                        title.set("visible", true);
                    }
                });

                exporting.events.on("exportfinished", function (event) {
                    title.set("visible", false);
                });

                // --- 12. Animacion de entrada ---
                series.appear(1000);
                newChart.appear(1000, 100);

            } catch (error) {
                console.error("Error al cargar las bibliotecas de AmCharts:", error);
            }
        };

        generateChart();

        // --- 13. Cleanup: destruir instancia al desmontar ---
        return () => {
            if (rootRef.current) {
                rootRef.current.dispose();
                rootRef.current = null;
            }
        };
    }, [chartData, startDate, endDate]);

    return <div id={chartDivId.current} style={{ width: "100%", height: "500px" }}></div>;
};

export default RoseChart;
